import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query'
import { getMapDatasetsQuery, getMapQuery, queryCacheUpdateMap, MAPS_ROOT_QUERY_KEY } from '../api/mapQueries'
import { useAppSelector } from 'app/state'
import { deleteMap, getMapsQuery, MAPS_REMOTE_PAGE_SIZE, updateMap, setMapPrivacy, getDemoMaps } from '../api/maps'
import { tagsQueryKey } from 'features/tags/api/tagsQueries'
import { MapListFilters, MapOwnerFilter, Map } from '../types'
import { useUserMapsPermissionsMapping } from './query/userMapsPermissionsMapping'
import { CommonPrivacy, CommonSharingScope } from 'features/common'
import { Group } from 'features/groups/types'

export function useMapWithDatasets(
  mapId: string | undefined,
  { allowCurrentMap = true }: { allowCurrentMap: boolean }
) {
  const currentMap = useAppSelector((state) => state.maps.currentMap)
  const currentMapDatasets = useAppSelector((state) => state.maps.currentMapDatasets)

  if (mapId === undefined && currentMap && allowCurrentMap) {
    mapId = currentMap?.id
  }
  const useCurrentMap = allowCurrentMap && currentMap && currentMap.id === mapId
  const mapQuery = useQuery({ ...getMapQuery(mapId), enabled: Boolean(mapId) && !useCurrentMap })
  const datasetsQuery = useQuery({
    ...getMapDatasetsQuery(mapId),
    enabled: Boolean(mapId) && !useCurrentMap
  })

  const isLoading = useCurrentMap ? false : mapQuery.isLoading || datasetsQuery.isLoading

  return {
    map: useCurrentMap ? currentMap : mapQuery.data,
    datasets: useCurrentMap ? currentMapDatasets : datasetsQuery.data,
    isLoading
  }
}

export function useDeleteMap() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteMap,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MAPS_ROOT_QUERY_KEY], exact: false })
      queryClient.invalidateQueries(tagsQueryKey.get, { exact: false })
    }
  })
}

export function useMapDatasets(mapId: string | undefined, { allowCurrentMap = true }: { allowCurrentMap?: boolean }) {
  const currentMapId = useAppSelector((state) => state.maps.currentMap?.id)
  const currentMapDatasets = useAppSelector((state) => state.maps.currentMapDatasets)

  if (mapId === undefined && currentMapId && allowCurrentMap) {
    mapId = currentMapId
  }
  const useCurrentMap = allowCurrentMap && currentMapId === mapId

  const datasetsQuery = useQuery({
    ...getMapDatasetsQuery(mapId),
    enabled: Boolean(mapId) && !useCurrentMap
  })

  if (useCurrentMap) {
    return { data: currentMapDatasets, isLoading: false }
  }
  return datasetsQuery
}

export function useMaps(queryFilters: MapListFilters, pageSize: number = MAPS_REMOTE_PAGE_SIZE) {
  const permissionsMapping = useUserMapsPermissionsMapping()

  const rawQueryFilters = {
    ...queryFilters,
    permissions: permissionsMapping(queryFilters.permissions || MapOwnerFilter.All)
  }
  return useInfiniteQuery([MAPS_ROOT_QUERY_KEY, rawQueryFilters, pageSize], (params) => getMapsQuery(params), {
    getNextPageParam: (lastPage) => {
      if (lastPage.meta.page.current < lastPage.meta.page.totalPages) {
        return lastPage.meta.page.current
      }
      return undefined
    },
    refetchOnWindowFocus: 'always'
  })
}

export function useEditMap() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (editMap: Pick<Map, 'id' | 'title' | 'description' | 'tags'>) => updateMap(editMap),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MAPS_ROOT_QUERY_KEY], exact: false })
      queryClient.invalidateQueries(tagsQueryKey.get, { exact: false })
    }
  })
}

export function useDemoMaps(aiEnabled = false) {
  return useQuery({
    queryKey: [MAPS_ROOT_QUERY_KEY, 'demo', aiEnabled],
    queryFn: () => getDemoMaps(aiEnabled)
  })
}

export interface PublishAndSetMapPrivacyProps {
  mapId: string
  privacy: CommonPrivacy
  publicPassword?: string | null
  sharingScope?: CommonSharingScope
  groups?: Group[]
  users?: Map['users']
}

export function usePublishAndSetMapPrivacy() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (props: PublishAndSetMapPrivacyProps) => {
      const { groups, users, ...otherProps } = props

      const response = (await setMapPrivacy({
        ...otherProps,
        groupIds: groups?.map((g) => g.id),
        userIds:
          props.privacy !== CommonPrivacy.Private && props.privacy !== CommonPrivacy.Public
            ? users?.map((u) => u.id)
            : undefined
      })) as { token?: string; lastPublishedAt?: string } | undefined

      return { response, props }
    },
    onSuccess: ({ response, props }) => {
      const { mapId: id, privacy, sharingScope, publicPassword, groups, users } = props

      const mapPatch: Partial<Map> = {
        id,
        privacy,
        sharingScope,
        ...(groups && { groups }),
        ...(users && { users }),
        ...(publicPassword !== undefined && {
          publishedWithPassword: Boolean(publicPassword),
          publicMapPassword: publicPassword ?? undefined
        }),
        ...(response && {
          token: response.token,
          lastPublishedAt: response.lastPublishedAt
        })
      }

      queryCacheUpdateMap(mapPatch)

      queryClient.invalidateQueries({ queryKey: [MAPS_ROOT_QUERY_KEY], exact: false })
      queryClient.invalidateQueries(tagsQueryKey.get, { exact: false })
    }
  })
}
